/**
 * LexiStar notifications inbox API client (ST-00251). Server: server/modules/lexistar/
 * notifications (inbox.ts), mounted at /api/lexistar. Shapes: `types/lexistar-notification-inbox`.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response
 * BODY. The bell's older client (services/lexistarNotifications.js listNotifications /
 * markNotificationsRead) talks to the same two routes.
 *
 *   listInbox(params)               → InboxPage  (filters, cursor | after, facets)
 *   markInboxRead({ids})            → MarkReadResult
 *   markInboxRead({all, filter?})   → MarkReadResult  (filter: only the matching unread ones)
 */
import api from './api';
import type {
  InboxFilterParams,
  InboxListParams,
  InboxPage,
  MarkReadResult,
} from '../types/lexistar-notification-inbox';

const LX = '/lexistar';

type Wire = Record<string, string | number | boolean>;

/** Drop empty values; lists become comma lists (the server takes either). */
export function toWire(params: InboxListParams | InboxFilterParams): Wire {
  const out: Wire = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return out;
}

/** GET /notifications with the inbox filters, newest first. */
export async function listInbox(params: InboxListParams): Promise<InboxPage> {
  const res = await api.get(`${LX}/notifications`, { params: toWire(params) });
  return res.data as InboxPage;
}

export type MarkReadBody =
  | { ids: string[] }
  | { all: true; filter?: InboxFilterParams };

/** POST /notifications:markRead. A `filter` marks only the unread notifications it matches. */
export async function markInboxRead(body: MarkReadBody): Promise<MarkReadResult> {
  const payload =
    'all' in body && body.filter
      ? { all: true as const, filter: toWire(body.filter) }
      : body;
  const res = await api.post(`${LX}/notifications:markRead`, payload);
  return res.data as MarkReadResult;
}
